/* eslint-disable no-nested-ternary */
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { layout, space, flexbox, border, color, position } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import Text from 'theme/text';
import User from 'theme/icons/user';
import Check from 'theme/icons/check';
import CheckCircle from 'theme/icons/check-circle';
import { CircleFlag } from 'react-circle-flags';
import { MdError, MdBlock } from 'react-icons/md';
import Paypal from 'theme/icons/payments/paypal';
import Ababank from 'theme/icons/payments/ababank';
import Bank from 'theme/icons/payments/bank';
import Sepa from 'theme/icons/payments/sepa';
import Sureremit from 'theme/icons/payments/sureremit';
import { checkPayeeIdRequest } from 'redux-api/action/billing';
import BtnLink from 'theme/buttons/btn-link';
import translations from 'translations';

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-width: 390px;
  min-height: 148px;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 16px 16px 16px 32px;

  ${space}
  ${border}
  ${color}
  ${layout}
`;

const Flex = styled.div`
  display: flex;

  ${flexbox}
  ${space}
  ${position}
`;

const StyledText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
`;

const StyledCircleFlag = styled(CircleFlag)`
  width: 22px;
  height: 22px;
`;

const SelectedStatus = styled.div`
  position: absolute;
  width: 48px;
  height: 48px;
  bottom: -24px;
  right: -24px;
  transform: rotate(45deg);

  ${layout}
  ${color}
`;

const WrapIcon = styled.div`
  position: absolute;
  bottom: -2px;
  right: 2px;
  color: #fff;
`;

const CircleStatus = styled.div`
  margin-left: 10px;
  width: 16px;
  height: 16px;
  border-radius: 16px;
  background-color: ${({ valid }) =>
    !valid ? themeGet('colors.grey_100') : themeGet('colors.primary_100')};
  display: flex;
  justify-content: center;
  align-items: center;

  ::after {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 12px;
    background-color: ${({ valid }) =>
      !valid ? themeGet('colors.grey_200') : themeGet('colors.primary_400')};
  }
`;

const VerifiedStatus = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${themeGet('space.s')}px;
  color: ${({ verified }) =>
    verified ? themeGet('colors.primary_400') : themeGet('colors.red_400')};
`;

function PaymentAccountCard({
  paymentAccount,
  id,
  style,
  label,
  paymentMethodCode,
  billingProfilesCounter,
  onClick,
  selected,
  errors,
  company,
  verified,
}) {
  const { push } = useRouter();
  const dispatch = useDispatch();

  const { payee } = useSelector((state) => state.billing);

  const { languageCode } = useSelector((state) => state.config);

  const { response: payeeResponse } = payee;

  const [errorPaymentCoerence, setErrorPaymentCoerence] = useState(
    !!(errors && (errors.paymentAccountPid || errors.countryCode)),
  );

  useEffect(() => {
    if (paymentAccount.paymentMethodCode === 'PAYONEER') {
      dispatch(
        checkPayeeIdRequest({
          payeeId: paymentAccount.payoneer,
        }),
      );
    }
  }, [dispatch, paymentAccount]);

  const paymentIcon = useMemo(() => {
    switch (paymentMethodCode) {
      case 'PAYPAL':
        return <Paypal />;
      case 'ABA':
        return <Ababank />;
      case 'BANK':
        return <Bank />;
      case 'PAYONEER':
        return <img src="/images/logo_payoneer.png" width="64" height="35" alt="logo" />;
      case 'SEPA':
        return <Sepa />;
      case 'SURE_REMIT':
        return <Sureremit />;
      default:
        return <div />;
    }
  }, [paymentMethodCode]);

  const handleClick = useCallback(() => {
    if (onClick) {
      setErrorPaymentCoerence(false);
      return onClick(id);
    }

    return push({ pathname: '/billing/summary-payment-account', query: { id } });
  }, [push, onClick, id, company, paymentMethodCode]);

  const handleEdit = useCallback(() => push({ pathname: '/billing/payment-account', query: { id } }), [push, id]);

  return (
    <Wrapper bg="white" {...style}>
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
      >
        <Flex flexDirection="row" alignItems="center">
          {paymentIcon}
          {paymentAccount.paymentMethodCode === 'PAYONEER' && (
            <CircleStatus valid={payeeResponse} />
          )}
          <Flex mx="s" flexDirection="row" alignItems="center">
            <StyledText mt="xxs" color={billingProfilesCounter > 0 ? 'grey_600' : 'grey_200'}>
              <User />
            </StyledText>
            <StyledText
              ml="xxs"
              fontSize="pLarge"
              lineHeight="pLarge"
              fontWeight="bold"
              color={billingProfilesCounter > 0 ? 'grey_600' : 'grey_200'}
            >
              {billingProfilesCounter}
            </StyledText>
          </Flex>

          {!!paymentAccount.countryCode && (
            <StyledCircleFlag countryCode={paymentAccount.countryCode.toLowerCase()} />
          )}

          <VerifiedStatus verified={verified !== false}>
            {verified === false ? (
              <MdBlock size={18} title="Not verified" />
            ) : (
              <CheckCircle />
            )}
          </VerifiedStatus>

          <Flex flexDirection="row">
            <BtnLink
              label={verified === false ? translations[languageCode].Verify : translations[languageCode].Select}
              style={{ minWidth: 'auto' }}
              onClick={handleClick}
              disabled={selected}
            />
            <BtnLink
              label={translations[languageCode].Edit}
              style={{ minWidth: 'auto' }}
              onClick={() => handleEdit()}
            />
          </Flex>
        </Flex>
      </Flex>

      <StyledText mt="xs" fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600">
        {label}
      </StyledText>

      {paymentAccount.ownerName && (
        <StyledText my="xs" fontSize="h3" lineHeight="h3" fontWeight="medium" color="grey_400">
          {paymentAccount.ownerName}
        </StyledText>
      )}

      <SelectedStatus
        display={selected || errorPaymentCoerence ? 'block' : 'none'}
        bg={
          selected && !errorPaymentCoerence
            ? 'primary_500'
            : selected && errorPaymentCoerence
            ? 'red_500'
            : 'transparent'
        }
      />
      <WrapIcon>
        {selected && !errorPaymentCoerence ? (
          <Check width="14" />
        ) : selected && errorPaymentCoerence ? (
          <MdError width="14" />
        ) : (
          ''
        )}
      </WrapIcon>
    </Wrapper>
  );
}

export default PaymentAccountCard;
